import { Stack } from "@chakra-ui/react";
import { ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { RanaAlert, RanaLoading } from "@nens/ranalib";

import { ModelSchematisationMetadata } from "./components/model-schematisation-metadata";
import { SchematisationDetailsHeader } from "./components/schematisation-details-header";
import { SchematisationDownloadsSection } from "./components/schematisation-downloads-section";
import { SchematisationModelSection } from "./components/schematisation-model-section";
import { SchematisationModelTasksSection } from "./components/schematisation-model-tasks-section";
import { SchematisationPreview } from "./components/schematisation-preview";
import { SchematisationSimulationsSection } from "./components/schematisation-simulations-section";
import { SchematisationSourceAlert } from "./components/schematisation-source-alert";
import { SchematisationTemplatesSection } from "./components/schematisation-templates-section";
import { SchematisationValidityAlerts } from "./components/schematisation-validity-alerts";
import { useSchematisationDetails } from "./hooks/use-schematisation-details";
import {
  SchematisationDetailsContext,
  SchematisationDetailsContextValue,
  SchematisationDetailsMode,
} from "./schematisation-details-context";

interface ModelSchematisationDetailsProps {
  schematisationId: number;
  mode: SchematisationDetailsMode;
  selectedRevisionId: number | undefined;
  headerActions: ReactNode | ((hasReachedStickyPosition: boolean) => ReactNode);
  title?: ReactNode; // Optional override for the title section, e.g. to show the file name as a title in project mode
  preview?: ReactNode; // Optional override for the preview section, e.g. to show a custom preview in project mode
}

/**
 * Unified schematisation details component for both project and catalogue modes.
 *
 * This component:
 * - Handles loading states
 * - Handles invalid/missing data states
 * - Fetches revision, 3Di model, and schematisation data
 * - Provides context to child sections
 * - Renders metadata and model sections appropriate to the mode
 */

export function ModelSchematisationDetails({
  schematisationId,
  mode,
  selectedRevisionId,
  preview,
  title,
  headerActions,
}: ModelSchematisationDetailsProps) {
  const { isLoading, schematisation, revision, threediModel } = useSchematisationDetails(
    schematisationId,
    selectedRevisionId
  );

  if (isLoading) {
    return <RanaLoading isCentered />;
  }

  if (!schematisation) {
    return (
      <RanaAlert status="error">
        <FormattedMessage
          id="file.schematisation_not_found"
          defaultMessage="This model schematisation could not be found. It may have been deleted or you may not have permission to view it."
        />
      </RanaAlert>
    );
  }

  if (!revision) {
    return (
      <RanaAlert status="error">
        <FormattedMessage
          id="file.schematisation_revision_not_found"
          defaultMessage="No revision of this model schematisation could be found"
        />
      </RanaAlert>
    );
  }

  const contextValue: SchematisationDetailsContextValue = {
    mode,
    schematisation,
    revision,
    threediModel,
  };

  return (
    <SchematisationDetailsContext.Provider value={contextValue}>
      <Stack gap={6}>
        <SchematisationDetailsHeader title={title ?? schematisation.name} actions={headerActions} />

        <SchematisationValidityAlerts />

        <SchematisationSourceAlert />

        {preview ?? <SchematisationPreview />}

        <ModelSchematisationMetadata />

        <SchematisationModelSection />

        <SchematisationModelTasksSection />

        <SchematisationDownloadsSection />

        <SchematisationSimulationsSection />

        <SchematisationTemplatesSection />
      </Stack>
    </SchematisationDetailsContext.Provider>
  );
}
